import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { X, ChevronRight, Check } from "lucide-react";

interface TourStepProps {
    stepIndex: number;
    totalSteps: number;
    title: string;
    content: string;
    onNext?: () => void;
    onPrev?: () => void;
    onSkip: () => void;
    onComplete?: () => void;
    style?: React.CSSProperties;
    className?: string;
    position?: "top" | "bottom" | "left" | "right" | "center";
}

export function TourStep({
    stepIndex,
    totalSteps,
    title,
    content,
    onNext,
    onPrev,
    onSkip,
    onComplete,
    style,
    className,
    position = "bottom"
}: TourStepProps) {
    // 말풍선 화살표 위치 클래스
    const arrowClass = {
        top: "after:bottom-[-8px] after:left-1/2 after:-translate-x-1/2 after:border-t-white after:border-b-transparent after:border-x-transparent",
        bottom: "after:top-[-8px] after:left-1/2 after:-translate-x-1/2 after:border-b-white after:border-t-transparent after:border-x-transparent",
        left: "after:right-[-8px] after:top-1/2 after:-translate-y-1/2 after:border-l-white after:border-r-transparent after:border-y-transparent",
        right: "after:left-[-8px] after:top-1/2 after:-translate-y-1/2 after:border-r-white after:border-l-transparent after:border-y-transparent",
        center: ""
    };

    return (
        <div
            className={cn(
                "absolute z-[9999] bg-white rounded-xl shadow-2xl w-[320px] animate-in fade-in zoom-in-95 duration-200 p-0 overflow-hidden border border-brand-primary/20",
                position !== "center" && "after:absolute after:border-[8px]",
                position !== "center" && arrowClass[position],
                className
            )}
            style={style}
        >
            {/* 헤더 */}
            <div className="bg-brand-primary/5 px-5 py-3 flex justify-between items-center border-b border-brand-primary/10">
                <span className="text-xs font-bold text-brand-primary bg-white px-2 py-0.5 rounded-full border border-brand-primary/20">
                    STEP {stepIndex + 1}/{totalSteps}
                </span>
                <button
                    onClick={onSkip}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="투어 종료"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            {/* 내용 */}
            <div className="p-5">
                <h3 className="font-bold text-lg text-gray-800 mb-2">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-6 whitespace-pre-wrap">
                    {content}
                </p>

                {/* 네비게이션 */}
                <div className="flex justify-between items-center">
                    <button
                        onClick={onSkip}
                        className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2"
                    >
                        건너뛰기
                    </button>
                    <div className="flex gap-2">
                        {onPrev && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={onPrev}
                                className="text-gray-600 font-semibold shadow-sm"
                            >
                                이전
                            </Button>
                        )}
                        {onNext && (
                            <Button
                                size="sm"
                                onClick={onNext}
                                className="bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold shadow-sm"
                            >
                                다음
                                <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        )}
                        {onComplete && (
                            <Button
                                size="sm"
                                onClick={onComplete}
                                className="bg-green-500 hover:bg-green-600 text-white font-semibold shadow-sm"
                            >
                                시작하기
                                <Check className="w-4 h-4 ml-1" />
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
